/**
 * A SIMULAÇÃO criada pela conversa, vista pela view (spec-007 do
 * `project-onboarding-with-ai-fee-fixo`, [T108]/[T113] — US-2/US-3).
 *
 * Terceira fatia da extração do [G-R8], no mesmo idioma das irmãs
 * (`-conversas.js`, `-anexos.js`): `data`/`computed`/`methods` entram no
 * componente por spread, no MESMO `this` — por isso `enviarPergunta` e
 * `carregando`, que ficaram no `.vue` com o resto do thread, são alcançados
 * sem import nenhum.
 *
 * ★ A ORDEM É O CONTRATO: **depositar → só com `status === 200`, mandar a
 * frase.** O commit exige `resultados` (as chaves que `simulate(S)` produz) e
 * ele é calculado no navegador, porque o motor é um só; já a confirmação
 * viaja como TEXTO, porque o servidor a reconhece antes de o modelo vê-la, e
 * nenhuma frase carrega um objeto de 25 chaves. Frase sem depósito faz o
 * servidor recusar por um motivo que o consultor não tem como entender — ele
 * veria "não foi possível salvar" sobre uma tela que mostrava os números.
 *
 * O depósito tem UM dono: `apiFeeFixoIa.depositarSimulacao` (idempotente por
 * `previewId`, na store). O captador já o roda ao chegar o preview; o clique
 * só passa por ele de novo quando o automático falhou.
 *
 * Contrato completo:
 * `.specify/.../spec-007-simulacao-pelo-chat/contracts/deposito-de-resultados.md`.
 */
import {
  apiFeeFixoIa,
  mensagemDeConfirmacao,
  mensagemDeRecusa,
} from '@modules/fee-fixo/api/apiFeeFixoIa'
import { OPERACAO_CRIAR_SIMULACAO } from '@modules/fee-fixo/constants/escritas-ia'
import { MSG_SIMULACAO_TROCOU_DE_CONVERSA } from '@modules/fee-fixo/constants/guia'

export const data = () => ({
  // O gesto de salvar em curso — depósito + turno da confirmação. Local, como
  // o `confirmandoMigracao` do card genérico: é estado do CLIQUE, não da
  // conversa.
  salvandoSimulacao: false,
})

export const computed = {
  /**
   * A pendência da simulação, ou `null`.
   *
   * Não passa por `OPERACOES_COM_CARD`: a simulação tem card PRÓPRIO — ela
   * monta os componentes de verdade do Fee Lab —, e a store guarda a pendência
   * dela num slice separado (`simulacaoPendente`), longe do preview de e-mail
   * que o painel lateral deposita em `pendenteDeConfirmacao`.
   */
  simulacaoPendente() {
    return this.$store.getters['feeFixoIa/simulacaoPendente']
  },
  /** A falha do depósito automático do preview CORRENTE — vazio no caso comum. */
  erroDoDepositoDaSimulacao() {
    return this.$store.getters['feeFixoIa/erroDoDeposito']
  },
}

export const methods = {
  /** O retry do cartão: a mesma porta do captador, nunca um segundo POST solto. */
  redepositarSimulacao({ previewId } = {}) {
    return apiFeeFixoIa.depositarSimulacao({ previewId })
  },
  /**
   * Salvar o cenário que a conversa montou.
   *
   * @param {Object} payload
   * @param {string} payload.previewId O id que o preview emitiu.
   */
  async confirmarSimulacao({ previewId } = {}) {
    if (this.salvandoSimulacao || this.carregando) return
    const vozes = mensagemDeConfirmacao(OPERACAO_CRIAR_SIMULACAO, previewId)
    if (!vozes || !previewId) return

    // ★ A conversa do CLIQUE, guardada antes do `await` — mesmo padrão dos
    // tokens do [G-R6]/[G-A7]: guarda e compara, nunca relê. Ler a store na
    // volta responderia "qual conversa está aberta agora", que é justamente a
    // pergunta errada.
    const conversaDoClique = this.conversationId

    this.salvandoSimulacao = true
    try {
      // Idempotente: se o captador já depositou este `previewId`, não há POST.
      const r = await apiFeeFixoIa.depositarSimulacao({ previewId })
      // ★ Depósito que não confirmou NÃO manda a frase — e o card FICA na
      // tela. Diferente do card genérico (onde a pendência cai no clique,
      // porque o único passo é a conversa), aqui existe uma etapa que pode
      // falhar antes de qualquer coisa chegar ao modelo; sumir com o card
      // deixaria o consultor sem como tentar de novo, com os números que ele
      // acabou de ver fora de alcance.
      if (!r || r.status !== 200) {
        this.$showSnack(r || {})
        return
      }
      // ★ Caso 3 do MUST (decisão do lead, 24/08/2026): o consultor trocou de
      // conversa com o depósito EM VOO. A frase é TEXTO e `enviarPergunta`
      // resolve o destino na hora em que roda — sem esta guarda ela cairia no
      // thread NOVO, com o modelo instruído a afirmar que "a simulação acima
      // está salva" sobre um cartão que não está ali (medido: a frase foi para
      // a conversa nova).
      //
      // Aborta em vez de mandar para o thread certo: gravar num lugar onde o
      // consultor não está olhando é o oposto do que o two-step existe para
      // garantir. Nada foi commitado, ele sabe por quê, e refazer reaproveita
      // o MESMO `preview_id` (o depósito é idempotente, o registro vive 30
      // min). A pendência já saiu da tela pelo `lerHistorico`.
      if (this.trocouDeConversaDurante(conversaDoClique)) return
      // Só agora: os números estão sob o `preview_id`, e a frase basta.
      //
      // ⚠️ Limitação REGISTRADA, não resolvida: se o turno da confirmação
      // falhar depois daqui (queda de stream), o card já saiu e o consultor
      // fica sem botão. Não dá para decidir por `enviarPergunta`: quando o
      // evento SSE `error` chega, `sendMessage` devolve `{}` **sem status**
      // ([G-A6]) — sucesso e falha são indistinguíveis no retorno. O que
      // segura o caso é o servidor: o depósito é **idempotente enquanto o
      // preview estiver vivo** — `depositar` exige preview do mesmo dono antes
      // de gravar, e o reenvio IDÊNTICO devolve 200 (números diferentes para o
      // mesmo id dão 400). Então pedir de novo pela conversa ("salva a
      // simulação que você mostrou") funciona **se o modelo reusar o mesmo
      // `preview_id`**, que ele tem no `tool_result` anterior.
      //
      // ⚠️ O que NÃO funciona é reaproveitar o depósito através de um preview
      // NOVO: aí o id muda, e o depósito antigo não serve para nada — o front
      // precisa depositar de novo com o id novo. Medido pelo backend em
      // 24/08/2026; a primeira versão desta nota era otimista demais e dizia
      // só "é idempotente", o que faria alguém contar com a recuperação num
      // caso em que ela não existe.
      //
      // Inventar aqui uma restauração da pendência exigiria ler o erro do
      // turno de dentro deste método — estado de outra camada, para um caso
      // que o servidor cobre parcialmente.
      this.$store.dispatch('feeFixoIa/resetSimulacaoPendente')
      await this.enviarPergunta(vozes.message, { exibir: vozes.exibir })
    } finally {
      this.salvandoSimulacao = false
    }
  },
  /**
   * A conversa mudou entre o clique e a volta do depósito?
   *
   * Extraído de `confirmarSimulacao` porque ela passou dos 14 statements do
   * ESLint — e o corte é natural: a guarda tem razão própria, e é ela que o
   * teste da corrida exercita. Avisa por dentro, para o chamador ficar com um
   * `if` de uma linha.
   *
   * @param {?string} conversaDoClique O `conversationId` de quando o consultor
   *   clicou em Salvar — guardado, nunca relido.
   * @returns {boolean} `true` quando abortou (e já avisou).
   */
  trocouDeConversaDurante(conversaDoClique) {
    if (this.conversationId === conversaDoClique) return false
    this.$showSnack({
      status: 400,
      message: MSG_SIMULACAO_TROCOU_DE_CONVERSA,
      value: null,
    })
    return true
  },
  /**
   * Descartar o cenário. Nada foi gravado — o preview não criou linha alguma.
   *
   * O que a recusa precisa é chegar ao MODELO: limpar só a store, que ele não
   * vê, o faz reoferecer no turno seguinte o mesmo preview que o consultor
   * acabou de descartar (S-QA-3, medido no app real com a migração em lote).
   *
   * A pendência cai ANTES do envio — o card some no clique, sem esperar um
   * turno que o consultor não pediu —, e por isso a guarda vive no MÉTODO
   * também (G-12): uma recusa disparada durante o streaming perderia o card
   * sem que a mensagem chegasse ao modelo.
   *
   * ⚠️ **A recusa NÃO é simétrica à confirmação, e a assimetria é medida**
   * (`b-deposito`, 24/08/2026): o front tem `MSG_RECUSAR_*` para as três
   * escritas e **o servidor não tem padrão de detecção para nenhuma delas**.
   * É deliberado — a recusa não grava nem desfaz nada, então não há o que
   * interceptar; ela existe para o MODELO ler. Por isso o texto daqui não
   * precisa ser transcrito de lugar nenhum, ao contrário da frase de
   * confirmação, que é contrato entre dois repos.
   */
  cancelarSimulacao() {
    if (this.carregando) return
    const vozes = mensagemDeRecusa(OPERACAO_CRIAR_SIMULACAO)
    if (!vozes) return
    this.$store.dispatch('feeFixoIa/resetSimulacaoPendente')
    return this.enviarPergunta(vozes.message, { exibir: vozes.exibir })
  },
}
